import { useCallback, useEffect } from 'react';
import { DarkMode } from '@aparajita/capacitor-dark-mode';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useColorMode, useColorModeValue } from '@chakra-ui/react';
import { NavigationBar } from '@hugotomazi/capacitor-navigation-bar';

import { IS_ANDROID, IS_NATIVE } from '../constants';

export function useStatusBarStyleConfiguration() {
  const backgroundColor = useColorModeValue('#ffffff', '#111111');
  const statusBarColor = useColorModeValue('#f1f1f1', '#111111');
  const { colorMode, setColorMode } = useColorMode();

  const setStyle = useCallback(async () => {
    /* istanbul ignore if -- @preserve */
    if (IS_NATIVE) {
      const shiftAutomatically = localStorage.getItem('shiftColorModeAutomatically') === 'true';

      if (shiftAutomatically) {
        const isDarkMode = await DarkMode.isDarkMode();
        setColorMode(isDarkMode.dark ? 'dark' : 'light');
      }

      await StatusBar.setStyle({ style: colorMode === 'light' ? Style.Light : Style.Dark });
      if (IS_ANDROID) {
        await StatusBar.setBackgroundColor({ color: statusBarColor });
        await NavigationBar.setColor({ color: backgroundColor });
      }
    }
  }, [colorMode, setColorMode, statusBarColor, backgroundColor]);

  // Set status bar style according to the current color mode setup in the app.
  useEffect(() => {
    setStyle();
  }, [colorMode, setStyle]);

  // This is to make sure the status bar style is set when the app is opened and refreshes.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setStyle();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [setStyle]);

  // This is to make sure the status bar style is set when the app comes back to foreground.
  useEffect(() => {
    let listener: PluginListenerHandle;

    async function addStatusBarStyleChangeListener() {
      listener = await App.addListener('appStateChange', async (state) => {
        if (state.isActive) {
          setStyle();
        }
      });
    }

    addStatusBarStyleChangeListener();

    return () => {
      listener?.remove();
    };
  }, [setStyle]);

  // Listen to native mode changes.
  useEffect(() => {
    const mediaQueryDark = window.matchMedia('(prefers-color-scheme: dark)');
    const mediaQueryLight = window.matchMedia('(prefers-color-scheme: light)');

    mediaQueryDark.addEventListener('change', setStyle);
    mediaQueryLight.addEventListener('change', setStyle);

    return () => {
      mediaQueryDark.removeEventListener('change', setStyle);
      mediaQueryLight.removeEventListener('change', setStyle);
    };
  }, [setStyle]);
}
